/**
 * Подарунок на день народження — вибір із трьох (owner 2026-09-13).
 *
 * «Нажаття на кнопку відкриває екран, де людина обирає подарунок: 14 днів за
 * 1 ₴, місяць зі знижкою 55% за 449 ₴ і два місяці за ціною одного за 990 ₴.
 * Людина обирає й оформлює».
 *
 * Подарунок ОДИН на рік: усі три живуть в одному 30-денному вікні й ділять
 * відмітку «забрано» — оплатив будь-який, решта закриваються до наступного ДР
 * (рішення власника). Тому `available` тут один на всі три, а не по кожному.
 */
import { useNavigate } from "react-router-dom";

import { ActionButton } from "@/components/ActionButton";
import { BackNav } from "@/components/BackNav";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components/PageHeader";
import { Spinner } from "@/components/Spinner";
import { TgEmoji } from "@/components/TgEmoji";
import { showAppAlert } from "@/lib/appAlert";
import { useCountdown } from "@/lib/countdown";
import { tg } from "@/lib/telegram";
import {
  birthdayGiftState,
  useBirthdayGift,
  usePromoActivate,
  type BirthdayGiftOption,
  type BirthdayGiftState,
} from "@/api/hooks";

const FULL_PRICE = 990;

/** Картка «подарунка немає» — своя для кожної причини (2026-09-26). Раніше
 *  будь-яка недоступність читалась як «подарунок уже забрано», хоча в
 *  людини просто минули 30 днів. */
const UNAVAILABLE: Record<
  Exclude<BirthdayGiftState, "available"> | "failed",
  { title: string; text: string }
> = {
  redeemed: {
    title: "Подарунок уже забрано",
    text: "Цьогорічний подарунок ти вже використав. Наступний чекає на тебе рівно через рік — у твій день народження.",
  },
  // Тексти — ті самі, що в попапі `BirthdayGiftModal`.
  expired: {
    title: "Подарунок уже не діє",
    text: "Термін цьогорічного подарунка вже минув. Наступний подарунок чекатиме на тебе в день народження.",
  },
  declined: {
    title: "Подарунок прибрано",
    text: "Від цьогорічного подарунка ти відмовився. Наступний подарунок чекатиме на тебе в день народження.",
  },
  none: {
    title: "Подарунок ще попереду",
    text: "Подарунок чекатиме на тебе в день народження.",
  },
  failed: {
    title: "Не вдалося завантажити подарунок",
    text: "Спробуй ще раз за хвилину.",
  },
};

/** Скільки відсотків знижки показувати на бейджі. Для 990 за 60 днів рахуємо
 *  від ДВОХ місяців — саме в цьому й подарунок.
 *  Для символічної гривні знижки НЕ показуємо: «−100%» і перекреслені 990 —
 *  це не знижка, а подарунок. */
function discount(o: BirthdayGiftOption): string | null {
  const price = Number(o.amount);
  const base = o.days >= 60 ? FULL_PRICE * 2 : FULL_PRICE;
  if (price <= 1 || !price || price >= base) return null;
  return `−${Math.round((1 - price / base) * 100)}%`;
}

function GiftCard({
  option,
  onPick,
  busy,
}: {
  option: BirthdayGiftOption;
  onPick: (slug: string) => void;
  busy: boolean;
}) {
  const price = Number(option.amount);
  const base = option.days >= 60 ? FULL_PRICE * 2 : FULL_PRICE;
  const off = discount(option);

  return (
    <div className="mb-3 rounded-ios-card bg-surface p-4 shadow-card">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-primary/10">
          <TgEmoji char="💎" size={24} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[0.9375rem] font-bold text-ink [text-wrap:balance]">
            {option.title}
          </div>
          <div className="mt-0.5 text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
            {option.subtitle}
          </div>
        </div>
      </div>

      <div className="my-3 flex items-center justify-center gap-3">
        <div
          className="font-display text-[2rem] font-bold leading-none text-primary tabular-nums"
          style={{ letterSpacing: "-0.02em" }}
        >
          {price} ₴
        </div>
        {off ? (
          <div className="flex flex-col items-start gap-1">
            <span className="font-mono text-[0.75rem] leading-none text-ink-dim line-through tabular-nums">
              {base} ₴
            </span>
            <span className="rounded-full bg-primary px-2 py-0.5 font-display text-[0.625rem] font-bold uppercase leading-none text-white">
              {off}
            </span>
          </div>
        ) : (
          <span className="rounded-full bg-primary/10 px-2.5 py-1 font-display text-[0.6875rem] font-bold uppercase leading-none text-primary">
            подарунок
          </span>
        )}
      </div>

      <ActionButton onClick={() => onPick(option.slug)} disabled={busy}>
        {busy ? "Готую оплату…" : `Обрати · ${option.days} днів`}
      </ActionButton>
    </div>
  );
}

export function BirthdayGiftPage() {
  const q = useBirthdayGift();
  const activate = usePromoActivate();
  const navigate = useNavigate();
  const left = useCountdown(q.data?.expires_at ?? undefined);
  // Без даних (помилка мережі) не вигадуємо стан — кажемо, що не завантажилось.
  const state = q.data ? birthdayGiftState(q.data) : "failed";
  const blocked = state === "available" ? null : UNAVAILABLE[state];

  const pick = (slug: string) => {
    tg()?.HapticFeedback?.impactOccurred?.("light");
    activate.mutate(slug, {
      onSuccess: (res) => {
        // Оплату відкриваємо так само, як у промо-подарунку: вбудований
        // браузер Telegram, інакше нове вікно.
        const w = tg();
        if (res.purchase_url) {
          if (w?.openLink) w.openLink(res.purchase_url);
          else window.open(res.purchase_url, "_blank", "noopener,noreferrer");
        }
      },
      onError: (e) =>
        showAppAlert((e as Error).message, { title: "Не вдалося оформити" }),
    });
  };

  return (
    <>
      <BackNav onBack={() => navigate(-1)} showMenu />
      <PageHeader
        title="Подарунок на день народження"
        eyebrow="Твій день"
        titleLead="Подарунок на"
        titleAccent="день народження"
        subtitle="Обери один із трьох — і він одразу твій."
      />

      {q.isLoading ? (
        <Spinner />
      ) : blocked || !q.data ? (
        <div className="rounded-ios-card bg-surface p-5 text-center shadow-card">
          <div className="mb-2">
            <TgEmoji char="🥲" size={32} />
          </div>
          <div className="font-display text-[0.9375rem] font-bold text-ink [text-wrap:balance]">
            {blocked?.title}
          </div>
          <div className="mt-1 text-[0.8125rem] leading-snug text-ink-dim [text-wrap:balance]">
            {blocked?.text}
          </div>
        </div>
      ) : (
        <>
          {left && (
            <div className="mb-3 flex items-center justify-center gap-2 rounded-full bg-primary/[0.08] px-4 py-2">
              <Icon name="clock" size={14} color="#FE6B04" />
              <span className="font-mono text-[0.75rem] text-primary">
                подарунок згорає {left}
              </span>
            </div>
          )}
          {q.data.options.map((o) => (
            <GiftCard
              key={o.slug}
              option={o}
              onPick={pick}
              busy={activate.isPending}
            />
          ))}
          <p className="mb-6 text-center text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
            Обрати можна щось одне — решта варіантів після оплати закриються до
            наступного дня народження.
          </p>
        </>
      )}
    </>
  );
}
